import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { db } from '../config/firebase';
import { collection, query, onSnapshot, addDoc, doc, updateDoc, deleteDoc, serverTimestamp, getDocs } from 'firebase/firestore';
import { formatCurrency, formatTasaForDisplay, parseAmountForState, parseTasaForState, formatNumberForDisplay, formatNumberForInput } from '../utils/formatters';
import { PencilIcon, TrashIcon } from './Icons';

const ModalOverlay = styled.div`
    position: fixed; top: 0; left: 0; right: 0; bottom: 0;
    background-color: rgba(17, 24, 39, 0.8);
    display: flex; justify-content: center; align-items: center;
    z-index: 2000;
`;

const ModalContent = styled.div`
    background-color: ${props => props.theme.colors.background};
    padding: 2rem;
    border-radius: 8px;
    width: 90%;
    max-width: 600px;
    max-height: 90vh;
    overflow-y: auto;
`;

const ModalTitle = styled.h3`
    font-family: ${props => props.theme.typography.titleFamily};
    font-size: 1.5rem;
    color: ${props => props.theme.colors.primary};
    margin: 0 0 1.5rem 0;
`;

const Form = styled.form`
    display: flex;
    gap: 1rem;
    margin-bottom: 2rem;
    align-items: flex-end;
`;

const InputGroup = styled.div`
    flex: 1;
    label {
        display: block;
        margin-bottom: 0.5rem;
        font-size: 0.875rem;
        font-weight: 500;
    }
    input {
        width: 100%;
        padding: 0.75rem;
        border: 1px solid ${props => props.theme.colors.border};
        border-radius: ${props => props.theme.borderRadius};
        box-sizing: border-box;
    }
`;

const Button = styled.button`
    font-family: ${props => props.theme.typography.fontFamily};
    background-color: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.textPrimary};
    border: none;
    border-radius: ${props => props.theme.borderRadius};
    padding: 0.75rem 1.5rem;
    cursor: pointer;
    transition: background-color 0.2s;
    &:disabled {
        background-color: #a0a0a0;
        cursor: not-allowed;
    }
`;

const List = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
`;

const ListItem = styled.li`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem;
    border-bottom: 1px solid ${props => props.theme.colors.border};
    &:last-child {
        border-bottom: none;
    }
`;

const ListItemText = styled.p`
    margin: 0;
    font-size: 1rem;
`;

const Actions = styled.div`
    display: flex;
    gap: 0.5rem;
`;

const IconButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    padding: 0.25rem;
`;

const ParcialesModal = ({ isOpen, onClose, caucion, userName }) => {
    const [parciales, setParciales] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [monto, setMonto] = useState('');
    const [tasa, setTasa] = useState('');
    const [editingParcialId, setEditingParcialId] = useState(null);

    useEffect(() => {
        if (!isOpen || !caucion?.id) {
            setParciales([]);
            return;
        }
        setIsLoading(true);
        const parcialesCollectionRef = collection(db, 'cauciones', caucion.id, 'parciales');
        const q = query(parcialesCollectionRef);
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const parcialesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setParciales(parcialesData);
            setIsLoading(false);
        }, (error) => {
            console.error("Error fetching parciales:", error);
            setIsLoading(false);
        });

        return () => unsubscribe();
    }, [isOpen, caucion]);

    const updateParentCaucion = async () => {
        const parcialesCollectionRef = collection(db, 'cauciones', caucion.id, 'parciales');
        const snapshot = await getDocs(parcialesCollectionRef);
        const parcialesData = snapshot.docs.map(doc => doc.data());

        let totalMontoOperado = 0;
        let weightedTasaSum = 0;

        for (const p of parcialesData) {
            totalMontoOperado += p.monto;
            weightedTasaSum += p.monto * p.tasa;
        }

        const tasaOperada = totalMontoOperado > 0 ? weightedTasaSum / totalMontoOperado : 0;

        const caucionDocRef = doc(db, 'cauciones', caucion.id);
        await updateDoc(caucionDocRef, {
            tasaOperada: tasaOperada,
            montoOperado: totalMontoOperado
        });
    };

    const handleSave = async (e) => {
        e.preventDefault();
        const montoValue = parseFloat(parseAmountForState(monto));
        const tasaValue = parseFloat(parseTasaForState(tasa));

        if (!montoValue || !tasaValue) {
            alert("Monto y Tasa son obligatorios.");
            return;
        }

        const parcialData = {
            monto: montoValue,
            tasa: tasaValue,
            createdBy: userName,
            createdAt: serverTimestamp()
        };

        const parcialesCollectionRef = collection(db, 'cauciones', caucion.id, 'parciales');

        if (editingParcialId) {
            const docRef = doc(db, 'cauciones', caucion.id, 'parciales', editingParcialId);
            await updateDoc(docRef, { monto: montoValue, tasa: tasaValue });
        } else {
            await addDoc(parcialesCollectionRef, parcialData);
        }

        await updateParentCaucion();

        // Reset form
        setMonto('');
        setTasa('');
        setEditingParcialId(null);
    };

    const handleEdit = (parcial) => {
        setEditingParcialId(parcial.id);
        setMonto(formatNumberForDisplay(parcial.monto));
        setTasa(formatTasaForDisplay(parcial.tasa));
    };

    const handleDelete = async (parcialId) => {
        if (window.confirm("¿Está seguro de que desea eliminar este parcial?")) {
            const docRef = doc(db, 'cauciones', caucion.id, 'parciales', parcialId);
            await deleteDoc(docRef);
            await updateParentCaucion();
        }
    };

    if (!isOpen) return null;

    return (
        <ModalOverlay>
            <ModalContent>
                <ModalTitle>Parciales de Caución - {caucion.cliente}</ModalTitle>

                <Form onSubmit={handleSave}>
                    <InputGroup>
                        <label>Monto Parcial</label>
                        <input
                            type="text"
                            inputMode="decimal"
                            value={monto}
                            onChange={(e) => setMonto(e.target.value)}
                            onFocus={(e) => setMonto(formatNumberForInput(e.target.value))}
                            onBlur={(e) => setMonto(formatNumberForDisplay(parseAmountForState(e.target.value)))}
                            placeholder="Ej: 1000000.50"
                            required
                        />
                    </InputGroup>
                    <InputGroup>
                        <label>Tasa Parcial (%)</label>
                        <input
                            type="text"
                            inputMode="decimal"
                            value={tasa}
                            onChange={(e) => setTasa(e.target.value)}
                            onFocus={(e) => setTasa(formatNumberForInput(e.target.value))}
                            onBlur={(e) => setTasa(formatTasaForDisplay(parseTasaForState(e.target.value)))}
                            placeholder="Ej: 85.5"
                            required
                        />
                    </InputGroup>
                    <Button type="submit">{editingParcialId ? 'Actualizar' : 'Agregar'}</Button>
                </Form>

                {isLoading ? <p>Cargando parciales...</p> : (
                    <List>
                        {parciales.length === 0 ? <p>No hay parciales cargados.</p> :
                        parciales.map(p => (
                            <ListItem key={p.id}>
                                <ListItemText>
                                    Monto: <strong>{formatCurrency(p.monto, caucion.moneda)}</strong> - Tasa: <strong>{formatTasaForDisplay(p.tasa)}</strong>
                                </ListItemText>
                                <Actions>
                                    <IconButton onClick={() => handleEdit(p)} title="Editar"><PencilIcon /></IconButton>
                                    <IconButton onClick={() => handleDelete(p.id)} title="Eliminar"><TrashIcon color="#DC2626" /></IconButton>
                                </Actions>
                            </ListItem>
                        ))}
                    </List>
                )}

                <div style={{ marginTop: '2rem', display: 'flex', justifyContent: 'flex-end' }}>
                    <Button onClick={onClose} style={{backgroundColor: '#465163'}}>Cerrar</Button>
                </div>
            </ModalContent>
        </ModalOverlay>
    );
};

export default ParcialesModal;
